import { Injectable } from '@angular/core';
import { IWorkflowItem} from '@interfaces/interfaces';
import { EWorkflowPathKey } from '@app/pages/participant/workflow/workflow-path-key.enum';
import { map } from 'rxjs/operators';
import { WorkflowService } from '@workflow/services/workflow.service';
import {
	IParticipantPageParameter,
	participantPages,
} from '@app/pages/participant/participants.routes';
import {TWorkflowViewMode} from '@workflow/components/navigator/interfaces';

interface IPossibleList {
	key: EWorkflowPathKey;
	previousURL: (param: Partial<IParticipantPageParameter>) => string;
	activeURL: (param: Partial<IParticipantPageParameter>) => string;
	nextURL: (param: Partial<IParticipantPageParameter>) => string;
}

@Injectable({
	providedIn: 'root'
})
export class WorkflowItemNavigationService {
	private workflowEntryPath: {wfID: string, list: IPossibleList[]}[] = [];
	private fallBackURL = participantPages.stammDaten.base;

	constructor(
		private workflowService: WorkflowService,
	) {
		this.workflowService.workflow$.pipe(
			map(workflow => workflow?.items?.map(item => ({wfID: item.id, list: this.buildLinkedList(item)})) ?? [])
		).subscribe(path => {
			this.workflowEntryPath = path;
		});
	}

	private buildLinkedList(item: IWorkflowItem): IPossibleList[] {
		const hasDetailPage = item.type === 'Questionnaire' && !item?.config?.disableDetailPage;
		const hasOverviewPage = item.type === 'Questionnaire' && !item?.config?.disableOverview;
		const finalPageBase = this.getFinalPageBase(item.type);

		// Aufbauen der Liste mit den Einträgen
		const posList: IPossibleList[] = [{
			key: EWorkflowPathKey.STAMMDATEN,
			previousURL: participantPages.stammDaten.base,
			activeURL: participantPages.stammDaten.base,
			nextURL: null,
		}];
		posList.push({
			key: EWorkflowPathKey.DETAIL,
			previousURL: null,
			activeURL: hasDetailPage ? participantPages.workflowDetails.base : null,
			nextURL: null,
		});
		posList.push({
			key: EWorkflowPathKey.OVERVIEW,
			previousURL: null,
			activeURL: hasOverviewPage ? participantPages.workflowOverview.base : null,
			nextURL: null,
		});
		posList.push({
			key: EWorkflowPathKey.RUN,
			previousURL: null,
			activeURL: finalPageBase,
			nextURL: finalPageBase,
		});

		// verdrahten der Nachfolger
		let lastActiveURL = posList[0].activeURL;
		for (let i = 1; i < posList.length; i++) {
			posList[i].previousURL = lastActiveURL;
			lastActiveURL = posList[i].activeURL ? posList[i].activeURL : lastActiveURL;
		}

		// verdrahten der Vorgänger
		let previousActiveURL = posList[posList.length - 1].activeURL;
		for (let i = posList.length - 2; i >= 0; i--) {
			posList[i].nextURL = previousActiveURL;
			previousActiveURL = posList[i].activeURL ? posList[i].activeURL : previousActiveURL;
		}

		return posList;
	}

	public getFirstAvailableEntryURL(item: IWorkflowItem, pos: EWorkflowPathKey = EWorkflowPathKey.DETAIL, viewMode?: TWorkflowViewMode): string {
		const obj = this.workflowEntryPath.find(path => path.wfID === item.id)?.list?.find(entry => entry.key === pos);
		const url = obj?.activeURL || obj?.nextURL || null;
		const param = this.buildPageParams(item, viewMode);

		return url !== null ? url(param) : this.fallBackURL(param);
	}
	public getFirstAvailableExitURL(item: IWorkflowItem, pos: EWorkflowPathKey, viewMode?: TWorkflowViewMode): string {
		const obj = this.workflowEntryPath.find(path => path.wfID === item.id)?.list?.find(entry => entry.key === pos);
		const url = obj?.activeURL || obj?.previousURL || null;
		const param = this.buildPageParams(item, viewMode);

		return url !== null ? url(param) : this.fallBackURL(param);
	}
	private buildPageParams(item: IWorkflowItem, viewMode?: TWorkflowViewMode): Partial<IParticipantPageParameter> {
		const param: Partial<IParticipantPageParameter> = { wfItemId: item.id };
		if (item.config?.disableWizard) {
			param.viewMode = 'compact';
		} else {
			param.viewMode = viewMode || item.recommendedViewMode || 'wizard';
		}

		return param;
	}

	private getFinalPageBase(type: string): (param: Partial<IParticipantPageParameter>) => string {
		switch (type) {
			case 'Questionnaire':
				return participantPages.workflowProcess.base;
			case 'Randomization':
				return participantPages.workflowRandomizationItem.base;
			default:
				throw new Error(`Question type "${type}" not supported (Item Navigation)`);
		}
	}

}
